import { supabase } from '../supabase/config';

/**
 * Teacher-to-line assignment writes.
 *
 * Every assignment used to be persisted by rewriting the grid's ENTIRE class
 * array from the browser's in-memory copy. A client whose copy was missing
 * assignments deleted them, and PostgREST answered 204 either way — so 212
 * assignments were destroyed between 3-8 Sep 2026 without a single error
 * reaching a user (issue #218).
 *
 * These functions call `pit.set_line_assignment`, which mutates only the one
 * cell server-side and writes the audit row in the same transaction. A stale
 * client can no longer erase work it never knew about, so callers must NOT
 * fall back to sending the whole array.
 */

export type LineAssignmentChange = {
  classId: string;
  lineId: string;
  /** null or '' clears the line. */
  teacherCode: string | null;
  teacherName?: string | null;
  className?: string | null;
  subject?: string | null;
};

export type AssignmentActor = {
  email?: string | null;
  /** Only a UUID is stored; anything else is recorded as null. */
  id?: string | null;
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const actorId = (actor?: AssignmentActor): string | null =>
  actor?.id && UUID_RE.test(actor.id) ? actor.id : null;

/**
 * Set or clear one teacher on one line of one class.
 * Resolves to the class's new lineAssignments map.
 */
export async function setLineAssignment(
  gridId: string,
  change: LineAssignmentChange,
  actor?: AssignmentActor
): Promise<Record<string, string>> {
  const { data, error } = await supabase.rpc('set_line_assignment', {
    p_grid_id: gridId,
    p_class_id: change.classId,
    p_line_id: change.lineId,
    p_teacher_code: change.teacherCode,
    p_teacher_name: change.teacherName ?? null,
    p_class_name: change.className ?? null,
    p_subject: change.subject ?? null,
    p_user_email: actor?.email ?? null,
    p_performed_by: actorId(actor),
  });

  if (error) throw new Error(error.message);
  return (data as Record<string, string>) ?? {};
}

/**
 * Apply many assignments in one transaction: all succeed or none do.
 */
export async function setLineAssignmentsBulk(
  gridId: string,
  changes: LineAssignmentChange[],
  actor?: AssignmentActor
): Promise<number> {
  if (changes.length === 0) return 0;

  const { data, error } = await supabase.rpc('set_line_assignments_bulk', {
    p_grid_id: gridId,
    p_changes: changes.map(c => ({
      classId: c.classId,
      lineId: c.lineId,
      teacherCode: c.teacherCode,
      teacherName: c.teacherName ?? null,
      className: c.className ?? null,
      subject: c.subject ?? null,
    })),
    p_user_email: actor?.email ?? null,
    p_performed_by: actorId(actor),
  });

  if (error) throw new Error(error.message);
  return (data as number) ?? 0;
}
